import { clearAuthAndRedirect, exitImpersonationIfActive, getAuthToken } from "@/stores/auth";
import { getSelectedServiceId } from "@/stores/service";
import axios, { type AxiosInstance } from "axios";
import { toast } from "sonner";

/**
 * Axios instance with a JWT bearer interceptor.
 * baseURL is the absolute API host from VITE_API_URL (falls back to the
 * relative "/api" so local dev keeps working through the Vite proxy — see
 * vite.config.ts).
 *
 * Admin calls attach the JWT from the auth store. Guest endpoints (/guest/*)
 * are public, so a missing token simply means no Authorization header.
 *
 * We deliberately do NOT pin a default Content-Type. Axios already sets
 * application/json for plain-object bodies, and — crucially — sets
 * multipart/form-data with the correct boundary for FormData bodies (file
 * uploads). A pinned application/json default clobbers that detection and
 * serializes FormData to "{}", which is why /admin/uploads returned 422
 * ("file" field missing).
 */
export const apiClient: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL || "/api",
  timeout: Number(import.meta.env.VITE_API_TIMEOUT) || 60000,
});

apiClient.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  const serviceId = getSelectedServiceId();
  if (serviceId) {
    config.headers["X-Service-Id"] = serviceId;
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const url: string = error?.config?.url ?? "";
    // A 403 on an admin call means the user lacks permission.
    // Guest endpoints are public, and /auth 403s (beta gate, disabled account)
    // are rendered inline by the login page — skip the toast for both.
    if (status === 403 && !url.startsWith("/guest") && !url.startsWith("/auth")) {
      toast.error("권한이 없습니다.", { id: "forbidden" });
    }
    // A 401 on an admin call means the session expired — clear + bounce to login.
    // Guest endpoints are public, so don't redirect on their failures.
    // Expired impersonation token → drop it and reload as the real account;
    // otherwise the session itself expired — clear + bounce to login.
    if (status === 401 && !url.startsWith("/guest") && !exitImpersonationIfActive()) {
      clearAuthAndRedirect();
    }
    return Promise.reject(error);
  }
);
